// The capture must be evaluated before every other module: it removes the MCP interaction secret
// from the address bar before Sentry, Amplify, the router or any other import can read the URL.
// eslint-disable-next-line simple-import-sort/imports -- the sort would move the capture below the others
import { isMcpAuthDocument } from './app/mcp/bootstrap/mcp-interaction-capture'
import { provideZoneChangeDetection } from '@angular/core'
import { bootstrapApplication } from '@angular/platform-browser'

import * as Sentry from '@sentry/angular'

import { initAmplify } from '@ti/core/auth/utils'

import { appConfig } from './app/app.config'
import { TspAdminAppComponent } from './app/common/components/app/tsp-admin-app.component'
import {
  sanitizeMcpBreadcrumb,
  sanitizeMcpEvent,
} from './app/mcp/bootstrap/mcp-telemetry-sanitizer'
import env from './env'
import { APP_VERSION } from './env/version'

const isE2eEnv = 'Cypress' in window
const isLocalhost =
  window?.location?.hostname === 'localhost' || window?.location?.hostname === '127.0.0.1'

// The MCP authorization documents carry a one-use secret and the account the user is signing in
// with, so nothing is recorded or replayed for them.
Sentry.init({
  dsn: env.sentry.dsn,
  release: `${env.sentry.project}@${APP_VERSION}`,
  enabled: env.sentry.enabled && !isLocalhost && !isE2eEnv,
  ignoreErrors: ['UserUnAuthenticatedException'],
  integrations: [
    Sentry.browserTracingIntegration(),
    ...(isMcpAuthDocument
      ? []
      : [
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
            networkDetailAllowUrls: env.sentry.networkDetailAllowUrls,
            // The MCP authorization calls carry session proofs and one-use secrets.
            networkDetailDenyUrls: [/\/mcp\/oauth\//],
          }),
          Sentry.replayCanvasIntegration(),
        ]),
  ],
  beforeBreadcrumb: sanitizeMcpBreadcrumb,
  beforeSend: sanitizeMcpEvent,
  beforeSendTransaction: sanitizeMcpEvent,
  // Performance Monitoring
  tracesSampleRate: isMcpAuthDocument ? 0 : 0.5, //  Capture 50% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: env.sentry.tracePropagationTargets,
  // Session Replay
  replaysSessionSampleRate: isMcpAuthDocument ? 0 : 0.1,
  replaysOnErrorSampleRate: isMcpAuthDocument ? 0 : 1.0,
})

// Login as is resolved from the `sessionId` query or from session storage and replaces Amplify's
// tokens. Connecting an app grants the signed-in person's own access, so the MCP authorization
// documents never take part in it: Amplify keeps its ordinary tokens, and the consent page
// refuses a tab that is in Login as.
initAmplify(
  {
    userPoolId: env.amplify.userPoolId,
    identityPoolId: env.amplify.identityPoolId,
    userPoolClientId: env.amplify.userPoolClientId,
  },
  isMcpAuthDocument
    ? undefined
    : {
        resolveSessionUrl: `${env.apiUrl}/tsp/user/login-session`,
        sessionIdParamName: 'sessionId',
      },
).then(() => {
  bootstrapApplication(TspAdminAppComponent, {
    ...appConfig,
    providers: [
      provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
      ...appConfig.providers,
    ],
  })
    .then(() => {
      const loadingElement = document.getElementById('loading-animation')
      if (loadingElement) {
        loadingElement.parentNode?.removeChild(loadingElement)
      }
    })
    .catch((e) => console.error(`Cannot bootstrap application`, e))
})
